/**
 * Focus follows the page (owner ruling 2026-11-03): opening one cycle, track
 * or session puts the focus navigation on it, with what it belongs to; the
 * page that lists all cycles, tracks or sessions clears that level. Focus used
 * to be a button on the cycle page and a pin on the lists, so opening a cycle
 * left the navigation about everything.
 *
 * Only entering is gated by the Cycle Focus Mode module, as the button was;
 * clearing never is, so nobody is left in a narrowed view.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { focusTargetForPath } from '@/lib/focusModeBarContext';
import { useModuleGate } from '@/lib/moduleGate';
import { usePinnedCycleStore } from '@/stores/prioritization/pinnedCycleStore';
import { useDegapProcessStore } from '@/stores/prioritization/degapProcessStore';
import { useTrackStore } from '@/stores/prioritization/trackStore';
import { useSessionStore } from '@/stores/prioritization/sessionStore';

export function useFocusFollowsInstance(): void {
  const { pathname } = useLocation();
  const enabled = useModuleGate('cycle_focus_mode');
  const cycles = useDegapProcessStore((s) => s.cycles);
  const tracks = useTrackStore((s) => s.tracks);
  const sessions = useSessionStore((s) => s.sessions);

  useEffect(() => {
    const target = focusTargetForPath(pathname);
    if (!target) return;
    const focus = usePinnedCycleStore.getState();

    if (target.kind === 'clear') {
      if (target.level === 'cycle' && focus.pinnedCycleId) focus.exitWorkspace();
      if (target.level === 'track' && focus.pinnedTrackId) focus.exitTrack();
      if (target.level === 'session' && focus.pinnedSessionId) focus.exitSession();
      return;
    }
    if (!enabled) return;

    const cycleName = (id: string | null | undefined) => (id ? cycles.find((c) => c.id === id)?.name ?? '' : '');

    if (target.kind === 'cycle') {
      const cycle = cycles.find((c) => c.id === target.id);
      if (!cycle) return;
      if (focus.pinnedCycleId === cycle.id && !focus.pinnedTrackId && focus.pinnedCycleName === cycle.name) return;
      focus.enterWorkspace(cycle.id, cycle.name);
      return;
    }

    if (target.kind === 'track') {
      const track = tracks.find((t) => t.id === target.id);
      if (!track?.cycleId) return;
      if (focus.pinnedTrackId === track.id && !focus.pinnedSessionId && focus.pinnedTrackName === track.name) return;
      focus.enterTrack(track.id, track.name, track.cycleId, cycleName(track.cycleId));
      return;
    }

    const session = sessions.find((s) => s.id === target.id);
    if (!session) return;
    const track = session.trackId ? tracks.find((t) => t.id === session.trackId) : undefined;
    const cycleId = session.cycleId ?? track?.cycleId ?? null;
    if (!track || !cycleId) return; // a session outside any cycle has no place in the cycle's focus
    if (focus.pinnedSessionId === session.id && focus.pinnedSessionName === session.name) return;
    focus.enterSession(session.id, session.name, track.id, track.name, cycleId, cycleName(cycleId));
  }, [pathname, enabled, cycles, tracks, sessions]);
}
